import _ from 'lodash'
import {RefElementBuilder} from '@wix/component-protocol/builder' // eslint-disable-line wix-editor/no-internal-import
import type {Builder, DalValue, ManifestFeature, Pointer} from '@wix/document-services-types'
import {
    ELEMENTS,
    type ElementItem,
    type Actions,
    type CustomActions,
    type DisplayFilters
} from '@wix/component-protocol/schema' // eslint-disable-line wix-editor/no-internal-import
import type {ElementBuilderData} from './ElementBuilderFactory'
import {isRefHostType} from '../../../../utils/refStructureUtils'

export const BUILDER_COMPONENT_MAP: Record<string, string> = {
    'mobile.core.components.Container': 'wixEditorElements.LegacyContainer',
    'wysiwyg.viewer.components.WRichText': 'wixEditorElements.WRichText',
    'wixui.StylableButton': 'wixEditorElements.Button',
    'wixui.CollapsibleText': 'wixEditorElements.CollapsibleText',
    'wixui.FiveGridLine': 'wixEditorElements.Line',
    'wysiwyg.viewer.components.LinkBar': 'wixEditorElements.SocialBar',
    'wysiwyg.viewer.components.ExpandableMenu': 'wixEditorElements.Menu',
    'wysiwyg.viewer.components.MediaPlayer': 'wixEditorElements.VimeoPlayer',
    'wysiwyg.viewer.components.SiteButton': 'wixEditorElements.Button',
    'wixui.SlotsPlaceholder': 'wixEditorElements.SlotsPlaceholder',
    'wixui.ImageX': 'wixEditorElements.Image',
    // Ref Component will be resolved in `toBuilderComponentType` method
    'wysiwyg.viewer.components.RefComponent': '',
    'wixui.StylableHorizontalMenu': 'wixEditorElements.Menu',
    'wixui.Menu': 'wixEditorElements.Menu',
    'wixui.MultiStateBox': 'wixEditorElements.LegacyMultiStateBox'
}

export class BaseElementBuilder {
    private readonly supportedTypes = Object.keys(BUILDER_COMPONENT_MAP)
    private readonly customTypeMapper?: (elementType: string, compPointer: Pointer) => string | undefined
    private readonly type: string
    private readonly role: string
    private readonly actions: Actions
    private readonly customActions: CustomActions
    private readonly manifest?: ManifestFeature
    private readonly displayFilters: DisplayFilters
    private readonly pointer: Pointer
    private readonly rootContainerPointer: Pointer
    private readonly data: DalValue
    private readonly elementsDefaults: Builder.ElementsDefaults

    constructor(data: ElementBuilderData) {
        this.customTypeMapper = data.customTypeMapper
        this.type = data.type
        this.role = data.role
        this.actions = data.actions
        this.customActions = data.customActions
        this.manifest = data.manifest
        this.pointer = data.pointer
        this.rootContainerPointer = data.rootContainerPointer
        this.data = data.data
        this.elementsDefaults = data.elementsDefaults
        this.displayFilters = data.displayFilters
    }

    static defaultBehaviors(): Builder.Behaviors {
        return {
            selectable: true,
            removable: true
        }
    }

    static defaultRootContainerBehaviors(): Builder.Behaviors {
        return {
            selectable: false,
            removable: false
        }
    }

    static toBuilderData(data: Builder.ElementsDefaults): Builder.DataItemOverrides {
        const metadataFields = ['id', 'type', 'metaData', 'rootCompId', 'pageId']
        return _(data)
            .omit(metadataFields)
            .mapValues(value => ({
                defaultValue: value
            }))
            .value()
    }

    getSelector(): string {
        return `.${this.pointer.id}`
    }

    getBehaviors(): Builder.Behaviors {
        if (this.pointer.id === this.rootContainerPointer.id) {
            return BaseElementBuilder.defaultRootContainerBehaviors()
        }

        if (!this.manifest) {
            return BaseElementBuilder.defaultBehaviors()
        }
        return {
            selectable: this.manifest.behavior?.closed?.selectable ?? true,
            removable: !this.manifest.behavior?.preventHide
        }
    }

    getDisplayName(): string {
        return this.manifest?.displayName?.value || this.role
    }

    toBuilderComponentType(): string {
        let modifiedType = this.type
        // For manifest it might be helpful to have a custom type mapper
        if (this.customTypeMapper) {
            modifiedType = this.customTypeMapper(this.type, this.pointer) ?? this.type
        }

        const isRefHost = isRefHostType(modifiedType)
        // For the case when WIW we assign page
        // Then on WBS it's resolved to dev-center type of nested widget
        if (isRefHost && this.data.pageId) {
            // Return pageId to which referenced component belongs
            return this.data.pageId
        }

        return BUILDER_COMPONENT_MAP[modifiedType] ?? modifiedType
    }

    // NOTE: Blocks builder support only refElement
    buildElement(): ElementItem | undefined {
        // Since Editor Elements CLI not support all types,
        // we ignore those that are not supported to avoid errors
        if (!this.supportedTypes.includes(this.type)) {
            return
        }

        const builder = new RefElementBuilder()

        const behaviors = this.getBehaviors()
        const componentType = this.toBuilderComponentType()
        const selector = this.getSelector()
        const displayName = this.getDisplayName()
        const dataOverrides = BaseElementBuilder.toBuilderData(this.data)

        const refElement = builder
            .setType(componentType)
            .setDisplayName(displayName)
            .setSelector(selector)
            .setBehaviors(behaviors)
            .setActions(this.actions)
            .setCustomActions(this.customActions)
            .setDisplayFilters(this.displayFilters)
            .setData(dataOverrides)
            .setElementsDefaults(this.elementsDefaults)
            .build()

        return {
            elementType: ELEMENTS.ELEMENT_TYPE.refElement,
            refElement
        }
    }
}
